import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import emailjs from "@emailjs/browser";

interface FormValues {
  fullName: string;
  phoneNumber: string;
  email: string;
  subject: string;
  description: string;
}

interface FormErrors {
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  description?: string;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    color: "#ccd6f6",
    "& fieldset": { borderColor: "rgba(100, 255, 218, 0.2)" },
    "&:hover fieldset": { borderColor: "rgba(100, 255, 218, 0.5)" },
    "&.Mui-focused fieldset": { borderColor: "#64ffda" },
  },
  "& .MuiInputLabel-root": { color: "#a8b2d8" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#64ffda" },
};

const Contact = () => {
  const [values, setValues] = useState<FormValues>({
    fullName: "",
    phoneNumber: "",
    email: "",
    subject: "",
    description: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | null>(null);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!values.fullName.trim()) newErrors.fullName = "Full name is required.";
    if (!values.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required.";
    } else if (!/^[+\d\s\-().]{7,20}$/.test(values.phoneNumber.trim())) {
      newErrors.phoneNumber = "Please enter a valid phone number.";
    }
    if (!values.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }
    if (!values.description.trim())
      newErrors.description = "Description is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setStatus(null);

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        {
          fullName: values.fullName,
          phone_number: values.phoneNumber,
          reply_to: values.email,
          subject: values.subject.trim() || "Portfolio",
          message: values.description,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!,
      );
      setStatus("success");
      setValues({
        fullName: "",
        phoneNumber: "",
        email: "",
        subject: "",
        description: "",
      });
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        px: { xs: 3, md: 10 },
        py: { xs: 6, md: 8 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 640 }}>
        <Typography
          variant="overline"
          sx={{ color: "#64ffda", letterSpacing: "4px", fontSize: "0.85rem" }}
        >
          Get in touch
        </Typography>

        <Typography
          variant="h3"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
            color: "#ffffff",
            mt: 1,
            mb: 2,
            fontSize: { xs: "2rem", md: "2.8rem" },
          }}
        >
          Contact
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: "#a8b2d8", mb: 6, fontSize: "0.97rem" }}
        >
          Have a project in mind or just want to say hello? Fill in the form and
          I'll get back to you.
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ maxWidth: 640, mx: "auto" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label="Full Name *"
              name="fullName"
              value={values.fullName}
              onChange={handleChange}
              error={!!errors.fullName}
              helperText={errors.fullName}
              fullWidth
              sx={inputSx}
              FormHelperTextProps={{ sx: { color: "#ff6b6b" } }}
            />

            <TextField
              label="Phone Number *"
              name="phoneNumber"
              value={values.phoneNumber}
              onChange={handleChange}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              fullWidth
              sx={inputSx}
              FormHelperTextProps={{ sx: { color: "#ff6b6b" } }}
            />

            <TextField
              label="Email Address *"
              name="email"
              type="email"
              value={values.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              sx={inputSx}
              FormHelperTextProps={{ sx: { color: "#ff6b6b" } }}
            />

            <TextField
              label="Description *"
              name="description"
              value={values.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description}
              multiline
              rows={5}
              fullWidth
              sx={inputSx}
              FormHelperTextProps={{ sx: { color: "#ff6b6b" } }}
            />

            {status === "success" && (
              <Alert
                severity="success"
                sx={{
                  backgroundColor: "rgba(100, 255, 218, 0.08)",
                  color: "#64ffda",
                  border: "1px solid rgba(100, 255, 218, 0.25)",
                  "& .MuiAlert-icon": { color: "#64ffda" },
                }}
              >
                Message sent! I'll be in touch shortly.
              </Alert>
            )}

            {status === "error" && (
              <Alert
                severity="error"
                sx={{
                  backgroundColor: "rgba(255, 107, 107, 0.08)",
                  color: "#ff6b6b",
                  border: "1px solid rgba(255, 107, 107, 0.25)",
                  "& .MuiAlert-icon": { color: "#ff6b6b" },
                }}
              >
                Something went wrong. Please try again.
              </Alert>
            )}

            <Button
              type="submit"
              disabled={loading}
              variant="outlined"
              endIcon={
                loading ? (
                  <CircularProgress size={16} sx={{ color: "#64ffda" }} />
                ) : (
                  <SendIcon />
                )
              }
              sx={{
                alignSelf: "flex-start",
                color: "#64ffda",
                borderColor: "rgba(100, 255, 218, 0.4)",
                px: 4,
                py: 1.2,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.95rem",
                "&:hover": {
                  borderColor: "#64ffda",
                  backgroundColor: "rgba(100, 255, 218, 0.08)",
                },
                "&.Mui-disabled": {
                  color: "rgba(100, 255, 218, 0.4)",
                  borderColor: "rgba(100, 255, 218, 0.15)",
                },
              }}
            >
              {loading ? "Sending…" : "Send Message"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Contact;
